import React, { useState } from 'react';
import { 
  Edit, Copy, Trash2, Eye, Package, 
  DollarSign, Info, Check, X, AlertCircle,
  Image as ImageIcon
} from 'lucide-react';
import { useMutation } from '@apollo/client';
import { UPDATE_PRODUCT, TOGGLE_PRODUCT_AVAILABILITY } from '../../apollo/mutations';

const ProductCard = ({ product, onEdit, onDelete, onView }) => {
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isEditingInventory, setIsEditingInventory] = useState(false);
  const [tempPrice, setTempPrice] = useState(product.basePrice);
  const [tempInventory, setTempInventory] = useState(product.inventory || 'In Stock');
  
  const [updateProduct] = useMutation(UPDATE_PRODUCT);
  const [toggleAvailability] = useMutation(TOGGLE_PRODUCT_AVAILABILITY);

  const handleQuickUpdate = async (field, value) => {
    try {
      await updateProduct({
        variables: {
          id: product.id,
          input: { [field]: value }
        }
      });
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  const handleToggleAvailable = async () => {
    try {
      await toggleAvailability({
        variables: {
          id: product.id,
          available: !product.available
        }
      });
    } catch (error) {
      console.error('Toggle error:', error);
    }
  };

  return (
        <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200 overflow-hidden">
        {/* Product Image */}
        <div className="relative h-48 bg-gray-100">
          {product.images && product.images.length > 0 ? (
            <img 
              src={
                // Handle the new image format with size properties
                typeof product.images[0] === 'string' 
                  ? product.images[0]
                  : product.images[0].url || 
                    product.images[0].full ||
                    product.images[0].original ||
                    product.images[0].thumbnail
              }
              alt={product.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/400x300?text=Product+Image';
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <ImageIcon className="text-gray-400" size={48} />
            </div>
          )}
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <button
            onClick={handleToggleAvailable}
            className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
              product.available 
                ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                : 'bg-red-100 text-red-800 hover:bg-red-200'
            }`}
          >
            {product.available ? 'Available' : 'Unavailable'}
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{product.name}</h3>
            <p className="text-sm text-gray-600">{product.category}</p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-1 ml-2">
            <button 
              onClick={() => onView(product)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="View Details"
            >
              <Eye size={16} className="text-gray-600" />
            </button>
            <button 
              onClick={() => onEdit(product)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Edit Product"
            >
              <Edit size={16} className="text-gray-600" />
            </button>
            <button 
              onClick={() => onDelete(product)}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete Product"
            >
              <Trash2 size={16} className="text-red-600" />
            </button>
          </div>
        </div>

        {/* Description Preview */}
        {product.description && (
          <p className="text-sm text-gray-700 mb-3 line-clamp-2">
            {product.description}
          </p>
        )}

        {/* Product Details Grid */}
        <div className="space-y-2">
          {/* Price - Inline Editable */}
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-gray-600 flex items-center">
              <DollarSign size={14} className="mr-1" />
              Base Price:
            </span>
            {isEditingPrice ? (
              <div className="flex items-center space-x-1">
                <input
                  type="number"
                  value={tempPrice}
                  onChange={(e) => setTempPrice(e.target.value)}
                  className="w-20 px-2 py-1 border rounded text-sm"
                  autoFocus
                />
                <button
                  onClick={() => {
                    handleQuickUpdate('basePrice', parseFloat(tempPrice));
                    setIsEditingPrice(false);
                  }}
                  className="text-green-600 hover:text-green-700"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => {
                    setTempPrice(product.basePrice);
                    setIsEditingPrice(false);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingPrice(true)}
                className="font-medium hover:text-blue-600 transition-colors"
              >
                ${product.basePrice}
              </button>
            )}
          </div>

          {/* Current Price if different */}
          {product.currentPrice && product.currentPrice !== product.basePrice && (
            <div className="flex justify-between items-center py-1">
              <span className="text-sm text-gray-600">Current Price:</span>
              <span className="font-medium text-green-600">${product.currentPrice}</span>
            </div>
          )}

          {/* Inventory Status - Inline Editable */}
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-gray-600 flex items-center">
              <Package size={14} className="mr-1" />
              Inventory:
            </span>
            {isEditingInventory ? (
              <div className="flex items-center space-x-1">
                <select
                  value={tempInventory}
                  onChange={(e) => setTempInventory(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                  autoFocus
                >
                  <option value="In Stock">In Stock</option>
                  <option value="Low Stock">Low Stock</option>
                  <option value="Out of Stock">Out of Stock</option>
                </select>
                <button
                  onClick={() => {
                    handleQuickUpdate('inventory', tempInventory);
                    setIsEditingInventory(false);
                  }}
                  className="text-green-600 hover:text-green-700"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => {
                    setTempInventory(product.inventory);
                    setIsEditingInventory(false);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingInventory(true)}
                className={`text-sm font-medium px-2 py-0.5 rounded hover:opacity-80 transition-opacity ${
                  product.inventory === 'In Stock' ? 'bg-green-100 text-green-800' :
                  product.inventory === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}
              >
                {product.inventory || 'In Stock'}
              </button>
            )}
          </div>

          {/* Min Order Quantity */}
          {product.minOrderQuantity && (
            <div className="flex justify-between items-center py-1">
              <span className="text-sm text-gray-600">Min Order:</span>
              <span className="text-sm">{product.minOrderQuantity} units</span>
            </div>
          )}

          {/* Total Sold */}
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-gray-600">Total Sold:</span>
            <span className="font-medium">{product.totalSold || 0}</span>
          </div>

          {/* Groups Active */}
          {product.activeGroups && (
            <div className="flex justify-between items-center py-1">
              <span className="text-sm text-gray-600">Active Groups:</span>
              <span className="font-medium text-blue-600">{product.activeGroups}</span>
            </div>
          )}
        </div>

        {/* Approval Status */}
        {product.approvalStatus && product.approvalStatus !== 'approved' && (
          <div className="mt-3 flex items-center text-sm text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
            <AlertCircle size={14} className="mr-1" />
            {product.approvalStatus === 'pending' ? 'Pending Approval' : 'Requires Review'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;